import * as Phaser from "phaser";
import { Session, store, Nodes } from "../index.js";
import { log } from "@tumbodash/browserPackages";
import { GameEvents } from "../../common/events.js";

export interface GameClientOptions {
	/** Number of simultaneous touch pointers (default: 3) */
	activePointers?: number;
	/** Phaser Scale mode override. Use Phaser.Scale.RESIZE for games that should fill the container. */
	scaleMode?: number;
}

/**
 * Passed to the mount function in createDOMGameClient.
 * Subscribe to game events and access variantConfig without touching sockets directly.
 */
export interface DOMClientContext {
	variantConfig: Record<string, unknown>;
	/** Subscribe to registry:sync — fired whenever player scores/state change. */
	onSync(handler: (data: unknown) => void): void;
	/** Subscribe to game:nextRound — fired by the host to advance the round. */
	onNextRound(handler: (payload: unknown) => void): void;
}

/**
 * Render function supplied to createDOMGameClient.
 * Receives the game container element and a context for game events.
 * Return a cleanup/unmount function, or nothing if cleanup is not needed.
 */
export type DOMClientMount = (
	container: HTMLElement,
	ctx: DOMClientContext,
) => (() => void) | void;

// ── Shared helpers ─────────────────────────────────────────────────────────────

/** Reads the current registry from session state and pushes sorted scores to the menu overlay. */
function syncMenu(sessionId: string) {
	const s = Session.get(sessionId);
	if (!s) return;
	const registry = (s.state as Record<string, Record<string, { score?: number }>>).registry;
	const menuObj = Object.keys(registry)
		.map((playerId) => ({ playerId, score: registry[playerId].score || 0 }))
		.sort((a, b) => b.score - a.score);
	s.menuRef?.current?.setDetail(menuObj);
}

/**
 * Shared setup run by both createGameClient and createDOMGameClient.
 *
 * Handles everything that is identical regardless of rendering strategy:
 *   - session lookup and activeSessionId
 *   - variantConfig extraction
 *   - REGISTRY_SYNC and NEXT_ROUND socket wiring
 *   - cleanup registration for socket listeners
 *
 * Returns the resolved variantConfig and a lightweight event router
 * that downstream code can use to forward socket events to either
 * Phaser's event bus or DOM callbacks.
 */
function setupClientCore(sessionId: string): {
	variantConfig: Record<string, unknown>;
	/** Forward socket events to registered handlers (used by both Phaser and DOM paths). */
	emit(event: string, payload: unknown): void;
	/** Register an event handler on the internal event router. */
	on(event: string, handler: (payload: unknown) => void): void;
} | undefined {
	const socket = store.socket!;
	const session = Session.get(sessionId);
	if (!session) return;

	(store as Record<string, unknown>).activeSessionId = sessionId;

	// Variant config is stored in state by host/init before Session.emit fires,
	// so it is always present by the time this function runs.
	const variantConfig =
		((session.state as Record<string, unknown>)?.variant as Record<string, unknown>) ?? {};

	// Minimal event router — maps event names to handler sets.
	// Phaser games forward to game.events; DOM games forward to callbacks.
	const handlers: Record<string, Set<(p: unknown) => void>> = {};

	function emit(event: string, payload: unknown) {
		handlers[event]?.forEach((fn) => fn(payload));
	}

	function on(event: string, handler: (payload: unknown) => void) {
		if (!handlers[event]) handlers[event] = new Set();
		handlers[event].add(handler);
	}

	const syncHandler = (payload: { id: string; data: unknown }) => {
		Session.update(payload.id, payload.data, ["state", "registry"]);
		syncMenu(payload.id);
		emit(GameEvents.REGISTRY_SYNC, payload.data);
	};

	const nextRoundHandler = (payload: unknown) => {
		emit(GameEvents.NEXT_ROUND, payload);
	};

	// Replace any stale listeners from a previous session before registering new ones.
	socket.off(GameEvents.REGISTRY_SYNC);
	socket.on(GameEvents.REGISTRY_SYNC, syncHandler);
	socket.off(GameEvents.NEXT_ROUND);
	socket.on(GameEvents.NEXT_ROUND, nextRoundHandler);

	session.cleanup.push(() => {
		socket.off(GameEvents.REGISTRY_SYNC, syncHandler);
		socket.off(GameEvents.NEXT_ROUND, nextRoundHandler);
	});

	return { variantConfig, emit, on };
}

// ── Public factory: Phaser ─────────────────────────────────────────────────────

/**
 * Factory that handles all common Phaser client init boilerplate:
 * session lookup, variantConfig, registry/round socket wiring, and cleanup.
 *
 * Usage in a shelf client/init.ts:
 *   import Main from "./phaser/main.js";
 *   import { createGameClient } from "@tumbodash/clientGameEngine";
 *   export default (sessionId: string) => createGameClient(sessionId, Main);
 */
export async function createGameClient(
	sessionId: string,
	SceneClass: unknown,
	options: GameClientOptions = {},
): Promise<Phaser.Game | undefined> {
	const core = setupClientCore(sessionId);
	if (!core) return;

	const session = Session.get(sessionId)!;
	const { variantConfig, on } = core;

	const config = Nodes.phaser.config(session.mountPoint as string);
	if (options.scaleMode !== undefined) {
		(config as Record<string, unknown>).scale = {
			...(config as Record<string, unknown>).scale as object,
			mode: options.scaleMode,
		};
	}
	(config as Record<string, unknown>).scene = [SceneClass];
	(config as Record<string, unknown>).input = {
		touch: true,
		mouse: true,
		activePointers: options.activePointers ?? 3,
	};
	const game = new Phaser.Game(config as Phaser.Types.Core.GameConfig);

	// Pass variant config to the scene via Phaser's registry DataManager.
	// Setting it here (before the first rAF tick) guarantees create() can read it.
	game.registry.set("variantConfig", variantConfig);

	// Self-correcting canvas size: if #phaser-mount changes size after Phaser's
	// initial read (late overlay commit, rotation, keyboard), re-measure and
	// rescale immediately instead of waiting for Phaser's own ~500ms bounds
	// poll (ScaleManager#step). refresh() alone is not enough — it reapplies
	// the cached parentSize rather than re-reading the DOM; getParentBounds()
	// is what actually re-measures, exactly as ScaleManager#step does internally.
	const mountEl = (config as Record<string, unknown>).parent as HTMLElement | null;
	if (mountEl && typeof ResizeObserver !== "undefined") {
		const observer = new ResizeObserver(() => {
			if (game.scale.getParentBounds()) game.scale.refresh();
		});
		observer.observe(mountEl);
		session.cleanup.push(() => observer.disconnect());
	}

	// Bridge the internal event router into Phaser's own event bus
	// so scenes can listen with: this.game.events.on("registry:sync", ...).
	on(GameEvents.REGISTRY_SYNC, (data) => game.events.emit("registry:sync", data));
	on(GameEvents.NEXT_ROUND, (payload) => game.events.emit(GameEvents.NEXT_ROUND, payload));

	session.cleanup.push(() => {
		log.game("Unmounting game client: %s", sessionId);
		if (game && !(game as unknown as { isDestroyed: boolean }).isDestroyed) {
			game.destroy(true);
			(game as unknown as { isDestroyed: boolean }).isDestroyed = true;
		}
	});

	return game;
}

// ── Public factory: DOM / React ────────────────────────────────────────────────

/**
 * Counterpart to createGameClient for games that render with DOM or React
 * instead of Phaser. Handles identical boilerplate (session, variantConfig,
 * socket wiring, cleanup), then delegates rendering to a caller-supplied
 * mount function.
 *
 * Usage in a shelf client/init.ts:
 *   import { createDOMGameClient } from "@tumbodash/clientGameEngine";
 *   import BlindBid from "./BlindBid.js";
 *
 *   export default (sessionId: string) =>
 *     createDOMGameClient(sessionId, (container, { variantConfig, onSync, onNextRound }) => {
 *       const root = ReactDOM.createRoot(container);
 *       root.render(<BlindBid variantConfig={variantConfig} onSync={onSync} onNextRound={onNextRound} />);
 *       return () => root.unmount();
 *     });
 */
export async function createDOMGameClient(
	sessionId: string,
	mount: DOMClientMount,
): Promise<void> {
	const core = setupClientCore(sessionId);
	if (!core) return;

	const session = Session.get(sessionId)!;
	const { variantConfig, on } = core;

	const gameRoot = document.getElementById(session.mountPoint as string);
	if (!gameRoot) return;

	// Create a child div that fills the remaining space below the type3 overlay.
	// #overlay-root occupies the top of the flex column; this div takes the rest.
	let mountEl = document.getElementById("dom-mount");
	if (!mountEl) {
		mountEl = document.createElement("div");
		mountEl.id = "dom-mount";
		mountEl.style.flex = "1";
		mountEl.style.overflow = "hidden";
		gameRoot.appendChild(mountEl);
	}

	// Build the context object passed to the mount function.
	// onSync / onNextRound wrap the internal router so callers never touch sockets directly.
	const ctx: DOMClientContext = {
		variantConfig,
		onSync: (handler) => on(GameEvents.REGISTRY_SYNC, handler as (p: unknown) => void),
		onNextRound: (handler) => on(GameEvents.NEXT_ROUND, handler as (p: unknown) => void),
	};

	const unmount = mount(mountEl, ctx);

	session.cleanup.push(() => {
		log.game("Unmounting DOM game client: %s", sessionId);
		unmount?.();
	});
}
